import React, { Fragment, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

export type AlertType = 'success' | 'error' | 'warning' | 'info' | 'confirm';

export interface AlertOptions {
  title?: string;
  message: string;
  type?: AlertType;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: (value?: string) => void;
  onCancel?: () => void;
  showCancel?: boolean;
  showInput?: boolean;
  inputPlaceholder?: string;
  inputValue?: string;
}

interface AlertProps {
  isOpen: boolean;
  options: AlertOptions | null;
  onClose: () => void;
}

const Alert: React.FC<AlertProps> = ({ isOpen, options, onClose }) => {
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      if (options?.inputValue) {
        setInputValue(options.inputValue);
      }
    } else {
      document.body.style.overflow = '';
      setInputValue('');
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, options]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (options?.showCancel && options?.onCancel) {
          options.onCancel();
        }
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, options, onClose]);

  if (!isOpen || !options) return null;

  const {
    title,
    message,
    type = 'info',
    confirmText = 'OK',
    cancelText = 'Cancel',
    onConfirm,
    onCancel,
    showCancel = false,
    showInput = false,
    inputPlaceholder = '',
  } = options;

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm(showInput ? inputValue : undefined);
    }
    onClose();
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    onClose();
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <i className="feather-check-circle font-xxl text-success"></i>;
      case 'error':
        return <i className="feather-x-circle font-xxl text-danger"></i>;
      case 'warning':
        return <i className="feather-alert-triangle font-xxl text-warning"></i>;
      case 'confirm':
        return <i className="feather-help-circle font-xxl text-primary"></i>;
      default:
        return <i className="feather-info font-xxl text-info"></i>;
    }
  };

  const getTitle = () => {
    if (title) return title;
    switch (type) {
      case 'success':
        return 'Success';
      case 'error':
        return 'Error';
      case 'warning':
        return 'Warning';
      case 'confirm':
        return 'Confirm';
      default:
        return 'Information';
    }
  };

  const getConfirmButtonClass = () => {
    switch (type) {
      case 'success':
        return 'btn-success';
      case 'error':
        return 'btn-danger';
      case 'warning':
        return 'btn-warning';
      case 'confirm':
        return 'btn-primary';
      default:
        return 'btn-primary';
    }
  };

  const modalContent = (
    <Fragment>
      <div
        className="modal fade show"
        style={{ 
          display: 'block', 
          zIndex: 10000,
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%'
        }}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="bh-alert-title"
        aria-describedby="bh-alert-message"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            if (!showCancel) {
              handleConfirm();
            }
          }
        }}
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content border-0 shadow-lg rounded-xxl" style={{ maxWidth: '400px' }}>
            <div className="modal-body text-center p-5">
              <div className="mb-3">{getIcon()}</div>
              <h5 id="bh-alert-title" className="fw-700 text-grey-900 font-lg mb-3">
                {getTitle()}
              </h5>
              <p id="bh-alert-message" className="text-grey-600 font-sm mb-4">
                {message}
              </p>
              {showInput && (
                <div className="mb-4">
                  <input
                    type="text"
                    className="form-control text-start"
                    placeholder={inputPlaceholder}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleConfirm();
                      }
                    }}
                    autoFocus
                  />
                </div>
              )}
              <div className="d-flex gap-2 justify-content-center">
                {showCancel && (
                  <button
                    className="btn btn-secondary"
                    onClick={handleCancel}
                  >
                    {cancelText}
                  </button>
                )}
                <button
                  className={`btn ${getConfirmButtonClass()}`}
                  onClick={handleConfirm}
                >
                  {confirmText}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className="modal-backdrop fade show"
        style={{ 
          zIndex: 9999, 
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%'
        }}
        onClick={!showCancel ? handleConfirm : undefined}
      ></div>
    </Fragment>
  );

  return createPortal(modalContent, document.body);
};

export default Alert;

